import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { createId } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { calculateTotalInterest } from '@core/util/money';
import { isSet } from '@core/util/value';
import { Address } from '@models/address';
import {
	Debt,
	DefaultDebt,
	MatterFileType,
	PersonMatterClient,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { MatterClients } from '@state/global/matter_clients';
import { DebtsDispatch, DebtsState } from '@state/matter/debts';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep, merge, cloneDeepWith } from 'lodash';
import { Observable } from 'rxjs';
import { TestHooks } from './debt-dialog.hooks';

export interface DebtDialogData {
	matterId: string;
	debtId?: string;
	payeeName: string;
	itemBeingPaid: string;
	description?: string;
	amount?: number;
	payeeAddress?: Address;
	payeePhone?: string;
	payeeEmail?: string;
	payeeFax?: string;
	executionNumber?: string;
	notes?: string;
}

@UntilDestroy()
@Component({
	selector: 'app-debt-dialog',
	templateUrl: './debt-dialog.component.html',
	animations: [openClose()],
	standalone: false,
})
export class DebtDialogComponent implements OnInit {
	testHooks = TestHooks;
	@Select(MatterClients.get_clients) clients$: Observable<PersonMatterClient[]>;
	@ViewSelectSnapshot(DebtsState.getDebt) debtFilterFn: (id: string) => Debt;
	@SelectSnapshot(AuthState.getLoggedInUser) loggedInUser: AuthUser;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;

	public clients: PersonMatterClient[];
	public debt: Debt;
	public payer: { [key: string]: boolean };
	public descriptionPlaceholder: string;
	public step: '1' | '2';
	public editing = false;
	public purchaseFileType = MatterFileType.PURCHASE;
	public addPerDiem = false;

	readonly ICON = ICON;

	ngOnInit(): void {
		this.debt = merge(
			cloneDeep(DefaultDebt),
			cloneDeep(this.debtFilterFn(this.data.debtId)),
		);

		if (this.debt.startDate) {
			this.addPerDiem = true;
		}

		if (!this.data.debtId) {
			this.debt.payee.name = this.data.payeeName;
			this.debt.payee.description = this.data.description ?? null;
			this.debt.item = this.data.itemBeingPaid;
			this.debt.amount = this.data.amount || 0;
			this.debt.payee.address = this.data.payeeAddress ?? null;
			this.debt.payee.phoneWork = this.data.payeePhone ?? null;
			this.debt.payee.email = this.data.payeeEmail ?? null;
			this.debt.payee.fax = this.data.payeeFax ?? null;

			this.setPlaceholder();
		}
		this.step = '1';
		this.clients$.pipe(untilDestroyed(this)).subscribe((clients) => {
			this.clients = clients.sort((a, b) => {
				if (a.lastName > b.lastName) {
					return 1;
				}
				if (a.lastName < b.lastName) {
					return -1;
				}
				if (a.firstName > b.firstName) {
					return 1;
				}
				if (a.firstName < b.firstName) {
					return -1;
				}

				return 0;
			});

			// converts the full client list to an object `id: boolean` for use as a model
			this.payer = this.clients
				.map((client) => ({ [client.id]: this.debt.payer.includes(client.id) }))
				.reduce((acc, cur) => ({ ...acc, ...cur }), {});
			if (!Object.values(this.payer).includes(true)) {
				this.payer = this.clients
					.map((client) => ({ [client.id]: client.primaryContact }))
					.reduce((acc, cur) => ({ ...acc, ...cur }), {});
			}
		});
	}

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: DebtDialogData,
		private debtsDispatch: DebtsDispatch,
		private dialogRef: MatDialogRef<DebtDialogComponent>,
	) {
		this.dialogRef.addPanelClass(['vw-80', 'vh-80']);
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	addressChanged(address: Address) {
		this.debt.payee.address = address;
		this.changed();
	}

	changed(): void {
		this.setPlaceholder();
		if (this.addPerDiem) {
			this.setGrandTotal();
		} else {
			this.debt.perDiem = null;
			this.debt.startDate = null;
			this.debt.endDate = null;
			this.debt.principalAmount = null;
		}
	}

	togglePerDiem(): void {
		if (!this.addPerDiem) {
			this.debt.principalAmount = 0;
			this.debt.perDiem = 0;
		}
		this.addPerDiem = !this.addPerDiem;
		this.changed();
	}

	nextStep(): void {
		this.step = '2';
	}

	previousStep(): void {
		this.step = '1';
	}

	setPlaceholder(): void {
		if (this.debt.item) {
			this.descriptionPlaceholder = [this.debt.item, this.debt.payee?.name]
				.filter((piece) => !!piece)
				.join(' in favour of ');
		} else {
			this.descriptionPlaceholder = '';
		}
	}

	setGrandTotal(): void {
		if (
			isSet(this.debt.startDate) &&
			isSet(this.debt.endDate) &&
			isSet(this.debt.principalAmount) &&
			isSet(this.debt.perDiem)
		) {
			this.debt.amount =
				+this.debt.principalAmount +
				+calculateTotalInterest(
					this.debt.startDate,
					this.debt.endDate,
					this.debt.principalAmount,
					this.debt.perDiem,
					true,
				);
		} else {
			this.debt.amount = 0;
		}
	}

	saveModal(form: NgForm): void {
		if (form.valid) {
			const debtId = this.debt.id || createId();

			let debt: Debt = {
				...this.debt,
				payer: Object.keys(this.payer).filter((id) => this.payer[id] === true),
			};

			if (!isSet(debt.payee?.description)) {
				debt.payee.description = this.descriptionPlaceholder;
			}

			if (!debt.undertaking) {
				debt.fulfilled = false;
			}

			debt = this.convertUndefinedToNull(debt);

			if (this.debt.id) {
				this.debtsDispatch.UpdateDebt(this.debt.id, debt);
				this.dialogRef.close();
			} else {
				debt.id = debtId;
				this.debtsDispatch.Create(debt);
				this.dialogRef.close(debtId);
			}
		}
	}

	convertUndefinedToNull(debt: Debt): Debt {
		return cloneDeepWith(debt, (val) => {
			if (val === undefined) {
				return null;
			}
		});
	}
}
